import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <Link to="/" className="logo-link">
              <h1>Pebble</h1>
            </Link>
          </div>
          <nav className="nav">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/terms" className="nav-link">Terms</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="legal-content">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: September 2026</p>

          <section>
            <h2>Our Commitment to Your Privacy</h2>
            <p>
              At Pebble, we understand that tracking your baby's milestones, photos, and daily activities involves 
              sharing deeply personal information. We are committed to protecting your privacy and being transparent 
              about how we handle your data.
            </p>
          </section>

          <section>
            <h2>Information We Collect</h2>
            
            <h3>Personal Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Email address, name, and account preferences</li>
              <li><strong>Baby Information:</strong> Baby's name, birth date, and basic profile information</li>
              <li><strong>Photos:</strong> Daily photos you choose to upload to track your baby's growth</li>
              <li><strong>Tracking Data:</strong> Feeding times and amounts, sleep duration, diaper changes, and milestones</li>
              <li><strong>Family Sharing:</strong> Information about family members you choose to share access with</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li><strong>Usage Data:</strong> How you interact with our app (features used, time spent)</li>
              <li><strong>Device Information:</strong> Device type, operating system, and app version</li>
              <li><strong>Analytics:</strong> Crash reports and performance data to improve our service</li>
            </ul>
          </section>

          <section>
            <h2>How We Use Your Information</h2>
            
            <h3>Core App Functionality</h3>
            <ul>
              <li>Provide baby tracking features (feeding, sleep, photos, milestones)</li>
              <li>Enable family sharing with people you authorize</li>
              <li>Sync your data across your devices</li>
              <li>Send you important updates about your account</li>
            </ul>

            <h3>Anonymized Research and Insights</h3>
            <p>
              We may analyze anonymized, aggregated data to provide helpful parenting insights such as:
            </p>
            <ul>
              <li>"Babies at 6 months typically sleep 10-12 hours per night"</li>
              <li>"Most babies start eating solid foods around 6 months"</li>
              <li>"Average feeding frequency decreases as babies grow"</li>
            </ul>
            <p>
              <strong>Important:</strong> This research uses only statistical, anonymous data. Your individual 
              information is never identifiable, and your personal photos are never included in any research or analysis.
            </p>
          </section>

          <section>
            <h2>What We Never Do With Your Data</h2>
            <div className="privacy-promises">
              <div className="promise-item">
                <h3>❌ We Never Share Your Photos</h3>
                <p>Your baby's photos are private and sacred. We never share, sell, analyze, or distribute your photos to anyone.</p>
              </div>
              <div className="promise-item">
                <h3>❌ We Never Sell Your Personal Data</h3>
                <p>We do not sell, rent, or trade your personal information to third parties for marketing or any other purposes.</p>
              </div>
              <div className="promise-item">
                <h3>❌ We Never Share Individual Data</h3>
                <p>Your baby's specific feeding, sleep, or milestone data is never shared in an identifiable way.</p>
              </div>
            </div>
          </section>

          <section>
            <h2>Advertising</h2>
            <p>
              Pebble's free tier is supported by ads, which are delivered by Google AdMob. Pebble Premium
              removes ads entirely.
            </p>
            <ul>
              <li>
                <strong>Advertising ID:</strong> to serve ads and measure their performance, AdMob may access
                your device's advertising identifier. You can reset or limit it in your device settings.
              </li>
              <li>
                <strong>What we don't send:</strong> we never share your baby's photos, tracking records, or
                any personally identifying account information with advertisers.
              </li>
              <li>
                <strong>Your control:</strong> on iOS you are asked for permission before any tracking-based
                ads are shown, and declining keeps ads non-personalized. Subscribing to Premium turns ads off
                altogether.
              </li>
            </ul>
            <p>
              Google's handling of advertising data is described in{' '}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google's privacy and terms
              </a>.
            </p>
          </section>

          <section>
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
              <li><strong>Access Control:</strong> Only authorized personnel can access systems, and only when necessary</li>
              <li><strong>Regular Audits:</strong> We regularly review our security practices and infrastructure</li>
              <li><strong>Secure Storage:</strong> We use Google Firebase's secure cloud infrastructure</li>
            </ul>
          </section>

          <section>
            <h2>Your Rights and Choices</h2>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct your information</li>
              <li>
                <strong>Deletion:</strong> Delete your account and all associated data at any time —{' '}
                <Link to="/delete-account">see how to delete your account</Link>
              </li>
              <li><strong>Portability:</strong> Export your data in a readable format</li>
              <li><strong>Opt-out:</strong> Opt out of anonymized research (though this helps improve the app for all parents)</li>
            </ul>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>
              Pebble is designed for parents to track their babies and young children. We do not knowingly collect 
              personal information directly from children under 13. If you believe we have inadvertently collected 
              such information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of any significant changes 
              through the app or via email. Your continued use of Pebble after changes become effective constitutes 
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this privacy policy or your data, 
              please contact us at:
            </p>
            <p>
              <strong>Email:</strong> privacy@pebble.baby<br/>
              <strong>Subject Line:</strong> Privacy Policy Inquiry
            </p>
          </section>

          <div className="back-to-home">
            <Link to="/" className="btn btn-primary">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;